/**
 * Shared brand logo — lowercase "hacking.community" wordmark in a rounded
 * geometric face (Quicksand) beside an `hc` terminal-box monogram.
 * Per-portal accent via `tone`; keeps the app's cohesive palette.
 */
import { useRef } from "react";

const TONES = {
    cyan:    { text: "text-cyan-400",    stroke: "#22d3ee", soft: "rgba(34,211,238,0.10)" },
    violet:  { text: "text-violet-300",  stroke: "#a78bfa", soft: "rgba(167,139,250,0.10)" },
    emerald: { text: "text-emerald-300", stroke: "#34d399", soft: "rgba(52,211,153,0.10)" },
    amber:   { text: "text-amber-300",   stroke: "#fbbf24", soft: "rgba(251,191,36,0.10)" },
};

const SIZES = {
    sm: { box: 32, word: "text-base", tag: "text-[8px]" },
    md: { box: 40, word: "text-[15px] min-[360px]:text-lg sm:text-xl", tag: "text-[9px]" },
};

/** Option-02 monogram: solid tile with a `>_` prompt and a blinking cursor.
 *  Glyphs mounted within the first seconds of a page load play a terminal
 *  "boot" — the chevron draws itself, then the cursor pops in and blinks. */
export function BrandGlyph({ tone = "cyan", px = 40 }) {
    const t = TONES[tone] || TONES.cyan;
    const boot = useRef(performance.now() < 4000).current;
    return (
        <svg width={px} height={px} viewBox="0 0 40 40" fill="none" aria-hidden="true" className="shrink-0">
            <rect x="2" y="2" width="36" height="36" rx="10" fill={t.stroke} />
            <path d="M11 13 L18 20 L11 27" stroke="#05070a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                className={boot ? "hc-boot-chevron" : ""} />
            <rect x="21" y="24.5" width="8" height="3" rx="1.5" fill="#05070a"
                className={boot ? "hc-boot-cursor" : "hc-cursor-blink"} />
        </svg>
    );
}

export default function BrandMark({ tone = "cyan", size = "md", stacked = false, tagline, badge = null, hideWordOnMobile = false, className = "" }) {
    const t = TONES[tone] || TONES.cyan;
    const s = SIZES[size] || SIZES.md;
    return (
        <span className={`flex items-center gap-2.5 ${className}`} data-testid="brand-mark">
            <span className="relative shrink-0">
                <BrandGlyph tone={tone} px={s.box} />
                {badge}
            </span>
            <span className={`${hideWordOnMobile ? "hidden lg:flex" : "flex"} flex-col justify-center leading-none`}>
                <span
                    data-notranslate
                    className={`${s.word} text-slate-100 whitespace-nowrap`}
                    style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: stacked ? 1.02 : 1 }}
                >
                    {stacked ? (
                        <>
                            hacking<span className={t.text}>.</span><br />community
                        </>
                    ) : (
                        <>hacking<span className={t.text}>.</span>community</>
                    )}
                </span>
                {tagline && (
                    <span className={`${s.tag} tracking-[0.25em] text-slate-500 whitespace-nowrap`} style={{ lineHeight: 1, marginTop: 3 }}>
                        {tagline}
                    </span>
                )}
            </span>
        </span>
    );
}
